import { Fragment, type ReactNode } from 'react';
import type {
    CoexistenceConditionalAddUserPlanItem,
    UserProductAssignEvaluation,
    UserProductAssignResponse,
} from 'src/models/ProductAssignment';

type Props = {
    response: UserProductAssignResponse;
};

const verdictLabel = (v: string | undefined): string => {
    if (!v) return '—';
    const u = v.toUpperCase();
    if (u === 'ALLOW') return 'Permitido';
    if (u === 'DENY') return 'Denegado';
    return v;
};

const tableHuman = (inTable: string | undefined): string => {
    if (!inTable) return '—';
    if (inTable === 'user_plan_periods') {
        return 'Periodos de plan del usuario (user_plan_periods)';
    }
    return inTable;
};

const actionHuman = (action: string | undefined): string => {
    if (!action) return '—';
    if (action === 'ADD_USER') return 'Agregar usuario (ADD_USER)';
    return action;
};

const activeStatusLabel = (isActive: number): string => {
    if (isActive === 1) return 'Sí';
    if (isActive === 0) return 'No (0)';
    return String(isActive);
};

const dlGridStyle = {
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '6px 16px',
    fontSize: 13,
} as const;

const dtMuted = { color: '#666', margin: 0 } as const;
const ddReset = { margin: 0 } as const;

function CoexistenceRuleBlock({
    rule,
    index,
}: {
    rule: CoexistenceConditionalAddUserPlanItem;
    index: number;
}) {
    return (
        <div
            style={{
                marginTop: index === 0 ? 0 : 12,
                padding: '12px 14px',
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid #eee',
                fontSize: 13,
                lineHeight: 1.5,
            }}
        >
            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Regla {index + 1}</p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>
                    <strong>Acción:</strong> {actionHuman(rule.action)}
                </li>
                <li>
                    <strong>Tabla afectada:</strong> {tableHuman(rule.in_table)}
                </li>
                <li>
                    <strong>Producto carrito (origen → destino):</strong>{' '}
                    {rule.from_cart_product_id ?? '—'} → {rule.to_cart_product_id ?? '—'}
                </li>
                {rule.coexistence_rule_id != null ? (
                    <li>
                        <strong>ID regla de coexistencia:</strong> {rule.coexistence_rule_id}
                    </li>
                ) : null}
                {rule.bucket ? (
                    <li>
                        <strong>Bucket:</strong> {rule.bucket}
                    </li>
                ) : null}
                {rule.blocked_by != null ? (
                    <li>
                        <strong>Bloqueado por (cart_product_id):</strong> {rule.blocked_by}
                    </li>
                ) : null}
                {rule.is_active !== undefined ? (
                    <li>
                        <strong>Activo en registro:</strong>{' '}
                        {activeStatusLabel(rule.is_active)}
                    </li>
                ) : null}
            </ul>
        </div>
    );
}

type MetadataRow = { key: string; label: string; value: ReactNode };

function metadataRowIf(
    key: string,
    label: string,
    include: boolean,
    value: ReactNode,
): MetadataRow | undefined {
    return include ? { key, label, value } : undefined;
}

function evalPeriodDiffersFromResponse(
    evalPeriodId: number | undefined | null,
    responsePeriodId: number | null | undefined,
): boolean {
    return evalPeriodId != null && evalPeriodId !== responsePeriodId;
}

function compactMetadataRows(rows: Array<MetadataRow | undefined>): MetadataRow[] {
    return rows.filter((r): r is MetadataRow => r !== undefined);
}

function buildResponseMetadataRows(
    response: UserProductAssignResponse,
    evalMeta: UserProductAssignEvaluation['metadata'] | undefined,
): MetadataRow[] {
    const evalPeriodId = evalMeta?.user_plan_periods_id;

    return compactMetadataRows([
        metadataRowIf('email', 'Usuario', Boolean(response.email), response.email),
        metadataRowIf('user_id', 'ID usuario', response.user_id != null, response.user_id),
        metadataRowIf(
            'cart_product_id',
            'Producto (cart_product_id)',
            response.cart_product_id != null,
            response.cart_product_id,
        ),
        metadataRowIf(
            'user_plan_periods_id',
            'Periodo de plan (user_plan_periods_id)',
            response.user_plan_periods_id != null,
            response.user_plan_periods_id,
        ),
        metadataRowIf(
            'eval_user_plan_periods_id',
            'Periodo (evaluación)',
            evalPeriodDiffersFromResponse(evalPeriodId, response.user_plan_periods_id),
            evalPeriodId,
        ),
        metadataRowIf(
            'type_package',
            'Tipo de paquete',
            Boolean(response.type_package),
            response.type_package,
        ),
        metadataRowIf(
            'admin_customer_email',
            'Correo admin (cliente)',
            Boolean(response.admin_customer_email),
            response.admin_customer_email,
        ),
        metadataRowIf('code', 'Código', Boolean(response.code), response.code),
        metadataRowIf(
            'verdict',
            'Veredicto',
            Boolean(response.verdict),
            verdictLabel(response.verdict),
        ),
        metadataRowIf(
            'product_assigned',
            'Producto asignado',
            response.product_assigned !== undefined,
            response.product_assigned ? 'Sí' : 'No',
        ),
        metadataRowIf(
            'assignment_created',
            'Asignación creada',
            response.assignment_created !== undefined,
            response.assignment_created ? 'Sí' : 'No',
        ),
        metadataRowIf(
            'rules_mode',
            'Modo de reglas',
            Boolean(response.rules_mode),
            response.rules_mode,
        ),
        metadataRowIf(
            'had_business_rules',
            'Reglas de negocio',
            response.had_business_rules !== undefined,
            response.had_business_rules ? 'Configuradas' : 'Sin reglas de negocio',
        ),
    ]);
}

function ResponseMetadataDl({
    response,
    evalMeta,
}: {
    response: UserProductAssignResponse;
    evalMeta: UserProductAssignEvaluation['metadata'] | undefined;
}) {
    const rows = buildResponseMetadataRows(response, evalMeta);

    return (
        <dl style={dlGridStyle}>
            {rows.map((row) => (
                <Fragment key={row.key}>
                    <dt style={dtMuted}>{row.label}</dt>
                    <dd style={ddReset}>{row.value}</dd>
                </Fragment>
            ))}
        </dl>
    );
}

function SummaryPrimaryMessage({ primaryMessage }: { primaryMessage: string }) {
    if (primaryMessage) {
        return <p style={{ margin: '0 0 14px' }}>{primaryMessage}</p>;
    }
    return (
        <p style={{ margin: '0 0 14px', color: '#666' }}>
            La operación finalizó correctamente según el servidor.
        </p>
    );
}

function EvaluationSection({
    evaluation,
    evalMeta,
    primaryMessage,
    evaluationMessage,
}: {
    evaluation: UserProductAssignEvaluation;
    evalMeta: UserProductAssignEvaluation['metadata'] | undefined;
    primaryMessage: string;
    evaluationMessage: string;
}) {
    const showEvaluationBody = evaluationMessage && evaluationMessage !== primaryMessage;
    const verdictLine = evaluation.verdict ? (
        <p style={{ margin: '0 0 4px', fontSize: 13 }}>
            <strong>Veredicto:</strong> {verdictLabel(evaluation.verdict)}
            {evaluation.code ? ` · ${evaluation.code}` : ''}
        </p>
    ) : null;
    const rulesMetaLine =
        evalMeta?.had_business_rules !== undefined ? (
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
                Reglas de negocio (metadata): {evalMeta.had_business_rules ? 'Sí' : 'No'}
                {evalMeta.rules_iteration ? ` · iteración: ${evalMeta.rules_iteration}` : ''}
            </p>
        ) : null;

    return (
        <div
            style={{
                marginTop: 16,
                paddingTop: 14,
                borderTop: '1px solid #e0e0e0',
            }}
        >
            <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 13 }}>Evaluación</p>
            {showEvaluationBody ? (
                <p style={{ margin: '0 0 8px', fontSize: 13 }}>{evaluationMessage}</p>
            ) : null}
            {verdictLine}
            {rulesMetaLine}
        </div>
    );
}

function CoexistenceConditionalSection({
    coexistence,
}: {
    coexistence: CoexistenceConditionalAddUserPlanItem[] | null | undefined;
}) {
    if (!Array.isArray(coexistence) || coexistence.length === 0) {
        return null;
    }

    return (
        <div style={{ marginTop: 16 }}>
            <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 13 }}>
                Coexistencia condicional (ADD_USER)
            </p>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: '#666' }}>
                El usuario quedó asociado al producto según la regla; el registro principal puede estar en la tabla
                indicada (no siempre es un insert de asignación «por producto» estándar).
            </p>
            {coexistence.map((rule, i) => (
                <CoexistenceRuleBlock key={`${rule.coexistence_rule_id ?? i}-${i}`} rule={rule} index={i} />
            ))}
        </div>
    );
}

function AssignmentJsonDetails({ response }: { response: UserProductAssignResponse }) {
    return (
        <details style={{ marginTop: 16, fontSize: 12 }}>
            <summary style={{ cursor: 'pointer', color: '#666' }}>Respuesta JSON completa</summary>
            <pre
                style={{
                    margin: '10px 0 0',
                    padding: '12px 14px',
                    background: '#f5f5f5',
                    borderRadius: 8,
                    fontSize: 12,
                    lineHeight: 1.45,
                    overflow: 'auto',
                    maxHeight: 'min(40vh, 240px)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {JSON.stringify(response, null, 2)}
            </pre>
        </details>
    );
}

export function AssignmentSuccessSummary({ response }: Props) {
    const coexistence = response.coexistence_conditional_add_user_plan;
    const evalMeta = response.evaluation?.metadata;
    const primaryMessage = typeof response.message === 'string' ? response.message.trim() : '';
    const evaluationMessage =
        typeof response.evaluation?.message === 'string' ? response.evaluation.message.trim() : '';

    return (
        <div style={{ fontSize: 14, lineHeight: 1.5, color: '#333' }}>
            <SummaryPrimaryMessage primaryMessage={primaryMessage} />
            <ResponseMetadataDl response={response} evalMeta={evalMeta} />
            {response.evaluation ? (
                <EvaluationSection
                    evaluation={response.evaluation}
                    evalMeta={evalMeta}
                    primaryMessage={primaryMessage}
                    evaluationMessage={evaluationMessage}
                />
            ) : null}
            <CoexistenceConditionalSection coexistence={coexistence} />
            <AssignmentJsonDetails response={response} />
        </div>
    );
}
